/* ════════════════════════════════════════════════════════════
   BURANSHH THEME — GLOBAL JAVASCRIPT
   No build step, no dependencies. Loaded with `defer`.
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var config = window.themeConfig || {};

  /* ── Small helpers ───────────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  // Defense-in-depth: the cart item template below builds HTML by string
  // concatenation from /cart.js data (product/variant titles, image URLs).
  // Shopify already sanitizes these on output, but escaping here means a
  // rogue line item property or unusual product title can never inject
  // markup into the DOM via innerHTML.
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Formats an integer cent amount using a Shopify money_format string,
   * e.g. "₹{{amount}}" or "${{amount_with_comma_separator}}". Mirrors
   * the subset of Shopify.formatMoney() this theme actually needs, so
   * the AJAX cart shows correctly-formatted currency without waiting
   * on a full page reload.
   */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') { cents = parseInt(cents, 10) || 0; }
    format = format || config.moneyFormat || '₹{{amount}}';

    function withDelimiters(numberStr, thousands, decimal) {
      var parts = numberStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    var value;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var kind = match ? match[1] : 'amount';

    switch (kind) {
      case 'amount_no_decimals':
        value = withDelimiters((cents / 100).toFixed(0), ',', '');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters((cents / 100).toFixed(2), '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters((cents / 100).toFixed(0), '.', '');
        break;
      default:
        value = withDelimiters((cents / 100).toFixed(2), ',', '.');
    }

    return format.replace(placeholder, value);
  }

  /* ── Focus trap (shared by drawers & modals) ────────────── */
  var lastFocusedEl = null;

  function trapFocus(container) {
    lastFocusedEl = document.activeElement;
    var focusable = qsa('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    // { preventScroll: true } stops the browser scrolling the page to bring
    // the focused element into view — the #1 cause of the "auto-scroll up" bug.
    // We use a short setTimeout so the drawer CSS transition has started before
    // focus lands, preventing any layout recalculation mid-animation.
    setTimeout(function () {
      first.focus({ preventScroll: true });
    }, 50);

    function handleKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    container.addEventListener('keydown', handleKeydown);
    container.__releaseFocusTrap = function () {
      container.removeEventListener('keydown', handleKeydown);
      if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
        // preventScroll: true — do NOT jump viewport back to the trigger element
        lastFocusedEl.focus({ preventScroll: true });
      }
    };
  }

  function releaseFocus(container) {
    if (container && container.__releaseFocusTrap) {
      container.__releaseFocusTrap();
    }
  }

  /* ── Scroll fade-up animations ───────────────────────────── */
  if ('IntersectionObserver' in window) {
    var fadeObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          fadeObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.05,       // trigger as soon as 5% is visible — less jarring
      rootMargin: '0px 0px -40px 0px'  // 40px offset from bottom edge
    });

    qsa('.fade-up').forEach(function (el) { fadeObserver.observe(el); });
  } else {
    // No IO support — show all immediately, no hidden content
    qsa('.fade-up').forEach(function (el) { el.classList.add('active'); });
  }

  /* ── Header scrolled state ──────────────────────────────── */
  var header = qs('.site-header');
  window.addEventListener('scroll', function () {
    if (!header) return;
    header.classList.toggle('scrolled', window.scrollY > 30);
  }, { passive: true });

  /* ── Header offset measurement ──────────────────────────────
     Anything that needs to sit "below the sticky header" (currently
     just the PDP gallery) reads var(--header-offset) instead of a
     hardcoded px guess. The announcement bar's height is variable
     (message length, dismiss state, wrap on narrow viewports), so we
     measure the real stack on load/resize/dismiss rather than assume. */
  function updateHeaderOffset() {
    var annBar = document.getElementById('annBar');
    var height = (header ? header.offsetHeight : 0) +
      (annBar && !annBar.classList.contains('ann-bar--hidden') ? annBar.offsetHeight : 0);
    // Save scroll position before setting CSS property — Safari iOS can jump
    var savedY = window.scrollY;
    document.documentElement.style.setProperty('--header-offset', (height + 24) + 'px');
    // Restore immediately if scroll shifted (only happens on initial load reflows)
    if (savedY > 0 && Math.abs(window.scrollY - savedY) > 2) {
      window.scrollTo({ top: savedY, behavior: 'instant' });
    }
  }
  updateHeaderOffset();
  window.addEventListener('resize', function () {
    // Debounce resize to prevent rapid-fire reflows
    clearTimeout(window._headerOffsetTimer);
    window._headerOffsetTimer = setTimeout(updateHeaderOffset, 120);
  });
  window.addEventListener('load', updateHeaderOffset);

  /* ── Back to top visibility + click ────────────────────── */
  (function () {
    var btn = qs('[data-back-to-top]');
    if (!btn) return;
    function onScroll() {
      btn.classList.toggle('visible', window.scrollY > 400);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }());

  /* ── Lazy image fade-in (opt-in, race-condition safe) ─── */
  (function () {
    // Only apply the opacity fade if IntersectionObserver is available.
    // Without it we leave images fully visible (no opacity:0 risk).
    if (!('IntersectionObserver' in window)) return;

    function revealImg(img) {
      img.classList.add('loaded');
    }

    // Use an IntersectionObserver so images entering the viewport get revealed
    // even if the 'load' event already fired before this script ran.
    var imgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var img = entry.target;
        imgObserver.unobserve(img);
        if (img.complete) {
          revealImg(img);
        } else {
          img.addEventListener('load', function () { revealImg(img); }, { once: true });
          img.addEventListener('error', function () { revealImg(img); }, { once: true });
        }
      });
    }, { rootMargin: '200px 0px' }); // pre-load reveal 200px before entering viewport

    // Add the opt-in class (enables opacity:0 → 1 transition) then observe
    qsa('img[loading="lazy"]').forEach(function (img) {
      // Skip hero slides (they handle their own visibility)
      if (img.closest('.hero-slide')) return;
      img.classList.add('js-lazy-fade');
      if (img.complete) {
        // Already loaded (cached) — reveal immediately, no transition
        img.classList.add('loaded');
        img.style.transition = 'none';
      } else {
        imgObserver.observe(img);
      }
    });
  }());

  /* ── Announcement bar dismiss ───────────────────────────── */
  (function () {
    var bar = document.getElementById('annBar');
    var closeBtn = document.getElementById('annClose');
    if (!bar) return;

    var KEY = 'bh_ann_dismissed';
    if (sessionStorage.getItem(KEY)) {
      bar.classList.add('ann-bar--hidden');
    }

    closeBtn && closeBtn.addEventListener('click', function () {
      bar.classList.add('ann-bar--hidden');
      sessionStorage.setItem(KEY, '1');
      updateHeaderOffset();
    });
  }());

  /* ── Desktop nav dropdowns (mega menu) ──────────────────── */
  function closeAllDropdowns() {
    qsa('.nav-item--dropdown.open').forEach(function (item) {
      item.classList.remove('open');
      var trigger = qs('.nav-dropdown-trigger', item);
      trigger && trigger.setAttribute('aria-expanded', 'false');
    });
  }

  qsa('.nav-item--dropdown').forEach(function (item) {
    var btn = qs('.nav-dropdown-trigger', item);
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = item.classList.contains('open');
      closeAllDropdowns();
      if (!isOpen) {
        item.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });
  document.addEventListener('click', closeAllDropdowns);

  /* ── Mobile nav drawer ───────────────────────────────────── */
  var mobileToggle = qs('[data-mobile-nav-trigger]');
  var mobileDrawer = qs('[data-mobile-nav-drawer]');

  function openMobileNav() {
    if (!mobileDrawer) return;
    mobileToggle && mobileToggle.classList.add('open');
    mobileDrawer.classList.add('open');
    mobileDrawer.setAttribute('aria-hidden', 'false');
    mobileToggle && mobileToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('drawer-open');
    trapFocus(mobileDrawer);
  }
  function closeMobileNav() {
    if (!mobileDrawer || !mobileDrawer.classList.contains('open')) return;
    mobileToggle && mobileToggle.classList.remove('open');
    mobileDrawer.classList.remove('open');
    mobileDrawer.setAttribute('aria-hidden', 'true');
    mobileToggle && mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('drawer-open');
    releaseFocus(mobileDrawer);
  }
  mobileToggle && mobileToggle.addEventListener('click', function () {
    mobileDrawer.classList.contains('open') ? closeMobileNav() : openMobileNav();
  });
  qsa('[data-mobile-nav-close]').forEach(function (el) {
    el.addEventListener('click', closeMobileNav);
  });

  /* ── Search modal ────────────────────────────────────────── */
  var searchModal = qs('[data-search-modal]');
  var searchInput = qs('[data-search-input]');

  function openSearch() {
    if (!searchModal) return;
    searchModal.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('drawer-open');
    trapFocus(searchModal);
    setTimeout(function () {
      searchInput && searchInput.focus({ preventScroll: true });
    }, 80);
  }
  function closeSearch() {
    if (!searchModal || !searchModal.classList.contains('active')) return;
    searchModal.classList.remove('active');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('drawer-open');
    releaseFocus(searchModal);
  }
  qsa('[data-search-trigger]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openSearch();
    });
  });
  qsa('[data-search-close]').forEach(function (el) {
    el.addEventListener('click', closeSearch);
  });

  /* ── Global Escape key ───────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeSearch();
    closeMobileNav();
    closeAllDropdowns();
    closeCartDrawer();
  });

  /* ════════════════════════════════════════════════════════════
     AJAX CART
     ════════════════════════════════════════════════════════════ */
  var cartDrawer = qs('[data-cart-drawer]');
  var cartItemsEl = qs('[data-cart-items]');
  var cartCountEls = qsa('[data-cart-count]');
  var cartSubtotalEl = qs('[data-cart-subtotal]');
  var cartShippingFill = qs('[data-shipping-fill]');
  var cartShippingLabel = qs('[data-shipping-label]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('drawer-open');
    trapFocus(cartDrawer);
  }
  function closeCartDrawer() {
    if (!cartDrawer || !cartDrawer.classList.contains('active')) return;
    cartDrawer.classList.remove('active');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('drawer-open');
    releaseFocus(cartDrawer);
  }
  qsa('[data-cart-trigger]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (config.cartType === 'page') {
        window.location.href = config.routes.cartUrl || '/cart';
        return;
      }
      openCartDrawer();
    });
  });
  qsa('[data-cart-close]').forEach(function (el) {
    el.addEventListener('click', closeCartDrawer);
  });

  function cartItemTemplate(item) {
    var title = escapeHtml(item.product_title);
    var variantTitle = escapeHtml(item.variant_title);
    var url = escapeHtml(item.url);
    var variantLine = item.variant_title && item.variant_title !== 'Default Title'
      ? '<p class="cart-item__variant">' + variantTitle + '</p>'
      : '';
    return (
      '<div class="cart-item" data-line-key="' + item.key + '">' +
        '<a href="' + url + '" class="cart-item__image">' +
          '<img src="' + (item.image ? escapeHtml(item.image.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_300x$1$2')) : '') + '" alt="' + title + '" loading="eager" width="88" height="110" decoding="async">' +
        '</a>' +
        '<div class="cart-item__details">' +
          '<a href="' + url + '"><h3 class="cart-item__title">' + title + '</h3></a>' +
          variantLine +
          '<div class="cart-item__footer">' +
            '<div class="qty-stepper" role="group" aria-label="' + (config.strings && config.strings.cartQuantity || 'Quantity') + '">' +
              '<button type="button" data-qty-decrease aria-label="' + (config.strings && config.strings.decreaseQuantity || 'Decrease quantity') + '">' + '\u2212' + '</button>' +
              '<input type="text" inputmode="numeric" value="' + item.quantity + '" data-qty-input readonly>' +
              '<button type="button" data-qty-increase aria-label="' + (config.strings && config.strings.increaseQuantity || 'Increase quantity') + '">+</button>' +
            '</div>' +
            '<span class="cart-item__price">' + formatMoney(item.final_line_price) + '</span>' +
          '</div>' +
          '<button type="button" class="cart-item__remove" data-qty-remove>' + (config.strings && config.strings.cartRemove || 'Remove') + '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function updateShippingBar(cart) {
    if (!config.freeShippingEnabled || !cartShippingFill || !cartShippingLabel) return;
    var threshold = config.freeShippingThreshold || 0;
    if (threshold <= 0) {
      cartShippingFill.parentElement.parentElement.style.display = 'none';
      return;
    }
    var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    cartShippingFill.style.width = pct + '%';
    if (cart.total_price >= threshold) {
      cartShippingLabel.innerHTML = '<strong>' + (config.strings.freeShippingReached || 'Free shipping unlocked!') + '</strong>';
    } else {
      var remaining = formatMoney(threshold - cart.total_price);
      cartShippingLabel.innerHTML = (config.strings.freeShippingRemaining || 'Add __AMOUNT__ more for free shipping').replace('__AMOUNT__', remaining);
    }
  }

  function updateCartUI(cart) {
    cartCountEls.forEach(function (el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    if (cartSubtotalEl) {
      cartSubtotalEl.textContent = formatMoney(cart.total_price);
    }

    if (cartItemsEl) {
      if (cart.item_count === 0) {
        cartItemsEl.innerHTML = '';
        var emptyState = qs('[data-cart-empty]');
        var footer = qs('[data-cart-footer]');
        emptyState && (emptyState.style.display = 'flex');
        footer && (footer.style.display = 'none');
      } else {
        var emptyState2 = qs('[data-cart-empty]');
        var footer2 = qs('[data-cart-footer]');
        emptyState2 && (emptyState2.style.display = 'none');
        footer2 && (footer2.style.display = 'block');
        cartItemsEl.innerHTML = cart.items.map(cartItemTemplate).join('');
      }
    }

    updateShippingBar(cart);
  }

  function fetchCart() {
    return fetch(config.routes.cartGet || '/cart.js').then(function (r) { return r.json(); });
  }

  function changeLineQuantity(key, quantity) {
    cartDrawer && cartDrawer.classList.add('is-loading');
    return fetch(config.routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartUI(cart);
        return cart;
      })
      .finally(function () {
        cartDrawer && cartDrawer.classList.remove('is-loading');
      });
  }

  function addToCart(formData) {
    return fetch(config.routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(function (r) {
        if (!r.ok) { return r.json().then(function (err) { throw err; }); }
        return r.json();
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        updateCartUI(cart);
        if (config.cartType === 'page') {
          window.location.href = config.routes.cartUrl || '/cart';
        } else {
          openCartDrawer();
        }
        return cart;
      });
  }

  /* Delegated events: qty stepper + remove, inside the cart drawer */
  cartItemsEl && cartItemsEl.addEventListener('click', function (e) {
    var row = e.target.closest('.cart-item');
    if (!row) return;
    var key = row.getAttribute('data-line-key');
    var input = qs('[data-qty-input]', row);
    var qty = parseInt(input.value, 10) || 1;

    if (e.target.closest('[data-qty-increase]')) {
      changeLineQuantity(key, qty + 1);
    } else if (e.target.closest('[data-qty-decrease]')) {
      changeLineQuantity(key, Math.max(0, qty - 1));
    } else if (e.target.closest('[data-qty-remove]')) {
      changeLineQuantity(key, 0);
    }
  });

  /* ── Standalone cart page (/cart) ──────────────────────────
     Separate from the drawer's data-cart-items / data-cart-subtotal
     so the two never collide when both are present in the DOM (the
     drawer markup is always rendered in layout/theme.liquid). The
     page re-fetches and reloads its own markup after a change rather
     than re-implementing the drawer's row template, since the page
     uses a <table> structure instead of cards. */
  var cartPageItemsEl = qs('[data-cart-page-items]');
  if (cartPageItemsEl) {
    var cartPageSubtotalEl = qs('[data-cart-subtotal-page]');
    var cartPageShippingFill = qs('[data-shipping-fill-page]');
    var cartPageShippingLabel = qs('[data-shipping-label-page]');

    cartPageItemsEl.addEventListener('click', function (e) {
      var row = e.target.closest('.cart-table__row');
      if (!row) return;
      var key = row.getAttribute('data-line-key');
      var input = qs('[data-qty-input]', row);
      var qty = parseInt(input.value, 10) || 1;
      var nextQty = null;

      if (e.target.closest('[data-qty-increase]')) {
        nextQty = qty + 1;
      } else if (e.target.closest('[data-qty-decrease]')) {
        nextQty = Math.max(0, qty - 1);
      } else if (e.target.closest('[data-qty-remove]')) {
        nextQty = 0;
      }
      if (nextQty === null) return;

      row.style.opacity = '0.5';
      fetch(config.routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: nextQty })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          updateCartUI(cart); // syncs header badge + drawer in the background too
          if (nextQty === 0) {
            row.remove();
          } else {
            row.style.opacity = '';
            var priceCell = qs('.cart-table__price', row);
            var lineItem = cart.items.filter(function (i) { return i.key === key; })[0];
            if (priceCell && lineItem) priceCell.textContent = formatMoney(lineItem.final_line_price);
          }
          if (cartPageSubtotalEl) cartPageSubtotalEl.textContent = formatMoney(cart.total_price);
          if (cartPageShippingFill && cartPageShippingLabel && config.freeShippingEnabled) {
            var threshold = config.freeShippingThreshold || 0;
            if (threshold > 0) {
              var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
              cartPageShippingFill.style.width = pct + '%';
              cartPageShippingLabel.innerHTML = cart.total_price >= threshold
                ? '<strong>' + (config.strings.freeShippingReached || 'Free shipping unlocked!') + '</strong>'
                : (config.strings.freeShippingRemaining || 'Add __AMOUNT__ more for free shipping').replace('__AMOUNT__', formatMoney(threshold - cart.total_price));
            }
          }
          if (cart.item_count === 0) window.location.reload();
        })
        .catch(function () { row.style.opacity = ''; });
    });
  }

  /* Intercept product forms (main product page + quick add) */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('[data-product-form]')) return;
    e.preventDefault();

    var submitBtn = qs('[type="submit"]', form);
    var formData = new FormData(form);
    var payload = {
      id: formData.get('id'),
      quantity: formData.get('quantity') || 1
    };

    if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }

    addToCart(payload)
      .catch(function (err) {
        console.error('Add to cart failed', err);
      })
      .finally(function () {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
      });
  });

  /* Keep the cart drawer in sync on initial load.
     Save/restore scroll position because updateCartUI rebuilds innerHTML
     which can cause the browser to reset scroll on some mobile browsers. */
  if (cartDrawer) {
    fetchCart().then(function (cart) {
      var savedY = window.scrollY;
      updateCartUI(cart);
      if (window.scrollY !== savedY) {
        window.scrollTo({ top: savedY, behavior: 'instant' });
      }
    }).catch(function () {});
  }

  /* ── Product gallery: thumbnail switching ───────────────── */
  qsa('[data-gallery]').forEach(function (gallery) {
    var thumbs = qsa('[data-gallery-thumb]', gallery);
    var slides = qsa('[data-gallery-slide]', gallery);
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = thumb.getAttribute('data-index');
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        slides.forEach(function (s) {
          var match = s.getAttribute('data-index') === index;
          s.hidden = !match;
        });
      });
    });
  });

  /* ── Product form quantity stepper (local only, no API call) ── */
  document.addEventListener('click', function (e) {
    var incBtn = e.target.closest('[data-quantity-increase]');
    var decBtn = e.target.closest('[data-quantity-decrease]');
    if (!incBtn && !decBtn) return;

    var wrap = (incBtn || decBtn).closest('.qty-stepper');
    if (!wrap) return;
    var input = qs('[data-quantity-input]', wrap);
    if (!input) return;

    var value = parseInt(input.value, 10) || 1;
    value = incBtn ? value + 1 : Math.max(1, value - 1);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ── Product page: variant → price/availability/media sync ── */
  qsa('[data-product-form]').forEach(function (form) {
    var variantSelect = qs('[data-variant-select]', form);
    if (!variantSelect) return;

    variantSelect.addEventListener('change', function () {
      var selected = variantSelect.options[variantSelect.selectedIndex];
      var price = selected.getAttribute('data-price');
      var compareAt = selected.getAttribute('data-compare-at');
      var available = selected.getAttribute('data-available') === 'true';
      var submitBtn = qs('[type="submit"]', form);
      var priceWrap = qs('[data-product-price]', form.closest('.product-content') || document);

      if (priceWrap && price) {
        var onSale = compareAt && parseInt(compareAt, 10) > parseInt(price, 10);
        priceWrap.innerHTML = onSale
          ? '<span class="price__compare">' + formatMoney(compareAt) + '</span><span class="price__sale">' + formatMoney(price) + '</span>'
          : '<span class="price__regular">' + formatMoney(price) + '</span>';
        priceWrap.classList.toggle('price--on-sale', !!onSale);
      }

      if (submitBtn) {
        submitBtn.disabled = !available;
        submitBtn.textContent = available
          ? submitBtn.getAttribute('data-label-available')
          : submitBtn.getAttribute('data-label-unavailable');
      }
    });
  });

  /* ── Collection sort ─────────────────────────────────── */
  var sortSelect = qs('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.assign(url.toString());
    });
  }

  /* ── Wishlist toggle (localStorage-backed, no app required) ──
     Persists saved product IDs under a single localStorage key so
     the heart/leaf button on the product page actually does
     something when pressed, and remembers state across visits. */
  (function () {
    var STORAGE_KEY = 'bh_wishlist';

    function readWishlist() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    }

    function writeWishlist(list) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        // Storage unavailable (private browsing, quota, etc.) — fail silently,
        // the button still gives visual feedback for the current page view.
      }
    }

    function setButtonState(btn, saved) {
      btn.classList.toggle('is-active', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var label = saved
        ? (config.strings && config.strings.removeFromWishlist)
        : (config.strings && config.strings.saveToWishlist);
      if (label) { btn.setAttribute('aria-label', label); }
    }

    var wishlistBtns = qsa('[data-wishlist-toggle]');
    if (!wishlistBtns.length) return;

    var saved = readWishlist();
    wishlistBtns.forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      setButtonState(btn, id && saved.indexOf(id) !== -1);

      btn.addEventListener('click', function () {
        var pid = btn.getAttribute('data-product-id');
        if (!pid) return;
        var list = readWishlist();
        var idx = list.indexOf(pid);
        var isSaved;
        if (idx === -1) {
          list.push(pid);
          isSaved = true;
        } else {
          list.splice(idx, 1);
          isSaved = false;
        }
        writeWishlist(list);
        setButtonState(btn, isSaved);
      });
    });
  }());

})();

/* ════════════════════════════════════════════════════════════
   MOBILE CAROUSEL / SCROLL-SNAP ENHANCEMENT — June 2026
   Adds dot indicators to gallery, featured collection,
   testimonials and catalogue sections on mobile.
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function initScrollCarousel(track, dotsEl, getItems) {
    var items = getItems();
    if (!items || items.length < 2) { dotsEl.innerHTML = ''; return; }

    dotsEl.innerHTML = '';
    items.forEach(function (_, i) {
      var dot = document.createElement('button');
      dot.className = 'carousel-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dot.addEventListener('click', function () {
        track.scrollTo({ left: items[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      });
      dotsEl.appendChild(dot);
    });

    var dots = qsa('.carousel-dot', dotsEl);

    function updateActiveDot() {
      var scrollLeft = track.scrollLeft;
      var closest = 0;
      var minDist = Infinity;
      items.forEach(function (item, i) {
        var dist = Math.abs((item.offsetLeft - track.offsetLeft) - scrollLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === closest); });
    }

    var t;
    track.addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(updateActiveDot, 80); }, { passive: true });
    updateActiveDot();
  }

  function setupMobileCarousels() {
    var isMobile = window.innerWidth <= 768;

    function initSection(gridSel, itemSel, dotClass, sectionSel) {
      qsa(gridSel).forEach(function (grid) {
        var wrap = sectionSel ? (grid.closest(sectionSel) || grid.parentElement) : grid.parentElement;
        var dotsEl = wrap ? wrap.querySelector('.' + dotClass) : null;
        if (!dotsEl) {
          dotsEl = document.createElement('div');
          dotsEl.className = dotClass + ' carousel-dots';
          grid.insertAdjacentElement('afterend', dotsEl);
        }
        if (isMobile) {
          initScrollCarousel(grid, dotsEl, function () { return qsa(itemSel, grid); });
        } else {
          dotsEl.innerHTML = '';
        }
      });
    }

    initSection('.gallery-grid',        '.gallery-item',     'gallery-carousel-dots',   '.gallery');
    initSection('.featured-collection .product-grid', '.product-card-wrap', 'fc-carousel-dots', '.featured-collection');
    initSection('.testimonial-grid',    '.testimonial-card', 'test-carousel-dots',      '.testimonials');
    initSection('.catalogue__grid',     '.catalogue__tile',  'cat-carousel-dots',       '.catalogue');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupMobileCarousels);
  } else {
    setupMobileCarousels();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setupMobileCarousels, 250);
  });
}());

/* ════════════════════════════════════════════════════════════
   BURANSHH — BRAND ELEVATION JS
   June 2026 — Animations, stagger reveals, enhancements
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── 1. STAGGER FADE-UP GROUP OBSERVER ─────────────────── */
  (function () {
    var groups = document.querySelectorAll('.fade-up-group');

    if (!('IntersectionObserver' in window)) {
      groups.forEach(function (el) { el.classList.add('active'); });
      return;
    }

    var staggerObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          staggerObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -30px 0px' });

    groups.forEach(function (el) {
      staggerObserver.observe(el);
      // Safety net: guarantee content is visible even if the observer
      // never fires (e.g. zero-height container at observe time).
      setTimeout(function () {
        if (!el.classList.contains('active')) { el.classList.add('active'); }
      }, 1500);
    });
  }());

  /* ── 2. PRODUCT CARD IMAGE LAZY FADE ───────────────────── */
  if ('IntersectionObserver' in window) {
    var cardImgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.complete) {
            img.classList.add('loaded');
          } else {
            img.addEventListener('load', function () { img.classList.add('loaded'); }, { once: true });
          }
          cardImgObserver.unobserve(img);
        }
      });
    }, { rootMargin: '200px 0px' });

    document.querySelectorAll('.product-image-hover img[loading="lazy"]').forEach(function (img) {
      img.classList.add('js-lazy-fade');
      cardImgObserver.observe(img);
    });
  }

  /* ── 3. CATALOGUE TILE STAGGER ─────────────────────────── */
  if ('IntersectionObserver' in window) {
    var catalogueSection = document.querySelector('.catalogue');
    if (catalogueSection) {
      var tiles = Array.from(catalogueSection.querySelectorAll('.catalogue__tile'));
      var catalogueObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            tiles.forEach(function (tile, i) {
              setTimeout(function () {
                tile.style.opacity = '1';
                tile.style.transform = 'translateY(0)';
              }, i * 90);
            });
            catalogueObs.unobserve(catalogueSection);
          }
        });
      }, { threshold: 0.08 });

      tiles.forEach(function (tile) {
        tile.style.opacity = '0';
        tile.style.transform = 'translateY(24px)';
        tile.style.transition = 'opacity 0.65s ease, transform 0.65s ease';
      });
      catalogueObs.observe(catalogueSection);
    }
  }

  /* ── 4. TESTIMONIAL CARD STAGGER ───────────────────────── */
  if ('IntersectionObserver' in window) {
    var testimonialSection = document.querySelector('.testimonials');
    if (testimonialSection) {
      var cards = Array.from(testimonialSection.querySelectorAll('.testimonial-card'));
      var testObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            cards.forEach(function (card, i) {
              setTimeout(function () {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, i * 100);
            });
            testObs.unobserve(testimonialSection);
          }
        });
      }, { threshold: 0.1 });

      cards.forEach(function (card) {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.35s ease';
      });
      testObs.observe(testimonialSection);
    }
  }

  /* ── 5. PILLAR/ABOUT STAGGER ───────────────────────────── */
  if ('IntersectionObserver' in window) {
    var aboutSection = document.querySelector('.about-mission');
    if (aboutSection) {
      var pillars = Array.from(aboutSection.querySelectorAll('.about-mission__pillar'));
      var aboutObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            pillars.forEach(function (p, i) {
              setTimeout(function () {
                p.style.opacity = '1';
                p.style.transform = 'translateY(0)';
              }, i * 110);
            });
            aboutObs.unobserve(aboutSection);
          }
        });
      }, { threshold: 0.1 });

      pillars.forEach(function (p) {
        if (window.innerWidth > 600) {
          p.style.opacity = '0';
          p.style.transform = 'translateY(18px)';
          p.style.transition = 'opacity 0.65s ease, transform 0.65s ease';
        }
      });
      aboutObs.observe(aboutSection);
    }
  }

  /* ── 6. PRODUCT CARD HOVER PARALLAX (desktop only) ─────── */
  if (window.matchMedia('(pointer: fine)').matches) {
    document.addEventListener('mousemove', function (e) {
      var card = e.target.closest('.product-card');
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width - 0.5) * 6;
      var y = ((e.clientY - rect.top)  / rect.height - 0.5) * 4;
      card.style.transform = 'perspective(800px) rotateY(' + x + 'deg) rotateX(' + (-y) + 'deg)';
    });
    document.addEventListener('mouseleave', function (e) {
      var card = e.target.closest('.product-card');
      if (card) {
        card.style.transform = '';
        card.style.transition = 'transform 0.4s ease';
      }
    }, true);
  }

  /* ── 7. NEWSLETTER INPUT ANIMATE ───────────────────────── */
  var newsletterInput = document.querySelector('.newsletter-form-row input[type="email"]');
  if (newsletterInput) {
    var formRow = newsletterInput.closest('.newsletter-form-row');
    newsletterInput.addEventListener('focus', function () {
      if (formRow) formRow.style.borderBottomColor = 'rgba(255,255,255,0.7)';
    });
    newsletterInput.addEventListener('blur', function () {
      if (formRow) formRow.style.borderBottomColor = 'rgba(255,255,255,0.3)';
    });
  }

  /* ── 8. SECTION COUNT-UP for brand story stats ──────────── */
  function animateCounter(el, target, duration) {
    var start = 0;
    var startTime = null;
    var isPercent = target.indexOf('%') !== -1;
    var num = parseFloat(target);
    if (isNaN(num)) return;
    function step(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(eased * num);
      el.textContent = current + (isPercent ? '%' : (target.replace(/[\d.]/g, '') || ''));
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  if ('IntersectionObserver' in window) {
    var statObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var orig = el.getAttribute('data-count-target') || el.textContent;
        el.setAttribute('data-count-target', orig);
        animateCounter(el, orig, 1400);
        statObs.unobserve(el);
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.brand-story-stat__number').forEach(function (el) {
      statObs.observe(el);
    });
  }

  /* ── 9. SMOOTH ANCHOR LINKS ─────────────────────────────── */
  document.addEventListener('click', function (e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    var target = document.querySelector(anchor.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ── 10. IMAGE LOAD FADE-IN (gallery + brand-story) ────── */
  document.querySelectorAll('.media-frame img[loading="lazy"]').forEach(function (img) {
    img.style.opacity = '0';
    img.style.transition = 'opacity 0.6s ease';
    if (img.complete) {
      img.style.opacity = '1';
    } else {
      img.addEventListener('load', function () { img.style.opacity = '1'; }, { once: true });
    }
  });

}());

/* ════════════════════════════════════════════════════════════
   BURANSHH — SCROLL & PARALLAX EFFECTS
   Scroll progress bar · parallax image drift ·
   count-up extension
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Scroll Progress Bar ────────────────────────────────── */
  (function () {
    var bar = document.createElement('div');
    bar.id = 'scroll-progress';
    document.body.prepend(bar);
    window.addEventListener('scroll', function () {
      var docH = document.documentElement.scrollHeight - window.innerHeight;
      var pct  = docH > 0 ? (window.scrollY / docH) * 100 : 0;
      bar.style.width = pct.toFixed(1) + '%';
    }, { passive: true });
  }());

  /* ── Subtle Parallax Drift for Media Frames ─────────────── */
  // Only runs on desktop with fine pointer, respects reduced-motion
  if (
    window.matchMedia('(pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    var parallaxFrames = Array.from(document.querySelectorAll('.media-frame img'));

    if (parallaxFrames.length) {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        requestAnimationFrame(function () {
          parallaxFrames.forEach(function (img) {
            var frame = img.closest('.media-frame');
            if (!frame) return;
            var rect = frame.getBoundingClientRect();
            var vh   = window.innerHeight;
            // Only apply if frame is visible
            if (rect.bottom < 0 || rect.top > vh) return;
            var progress = (vh - rect.top) / (vh + rect.height);
            var shift = (progress - 0.5) * 24; // max ±12px
            img.style.transform = 'translateY(' + shift.toFixed(1) + 'px)';
          });
          ticking = false;
        });
        ticking = true;
      }, { passive: true });
    }
  }

  /* ── Extended Count-up: also targets .about-values counters ─ */
  function animateCount(el, target, duration) {
    var num = parseFloat(target);
    var suffix = target.replace(/[\d.]+/, '');
    if (isNaN(num)) return;
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var ease = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(ease * num) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  if ('IntersectionObserver' in window) {
    var countObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        var orig = el.dataset.countTarget || el.textContent.trim();
        el.dataset.countTarget = orig;
        animateCount(el, orig, 1600);
        countObs.unobserve(el);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('.brand-story-stat__number, .about-stat-num').forEach(function (el) {
      countObs.observe(el);
    });
  }

}());

/* ════════════════════════════════════════════════════════════
   BURANSHH — SECTION MICRO-INTERACTIONS
   Section-label reveals · About Us animations · card tilt
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── 1. SECTION LABEL ANIMATED REVEAL ────────────────────
     Each .section-label gets a gentle fade-up when it enters
     the viewport.
  ────────────────────────────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    var labelObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('label-revealed');
          labelObs.unobserve(e.target);
        }
      });
    }, { threshold: 0.5, rootMargin: '0px 0px -20px 0px' });

    document.querySelectorAll('.section-label').forEach(function (el) {
      labelObs.observe(el);
    });
  } else {
    document.querySelectorAll('.section-label').forEach(function (el) {
      el.classList.add('label-revealed');
    });
  }

  /* ── 2. ABOUT MISSION — pillar hover enhancement ─────────
     Add a subtle background highlight on the hovered pillar.
  ────────────────────────────────────────────────────────── */
  document.querySelectorAll('.about-mission__pillar').forEach(function (pillar) {
    pillar.addEventListener('mouseenter', function () {
      pillar.style.background = 'rgba(103,106,38,0.03)';
    });
    pillar.addEventListener('mouseleave', function () {
      pillar.style.background = '';
    });
  });

  /* ── 3. ABOUT HERO — PARALLAX ON IMAGE ───────────────────
     Subtle image parallax on hero scroll.
  ────────────────────────────────────────────────────────── */
  if (
    window.matchMedia('(pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    var heroImg = document.querySelector('.about-hero__img');
    if (heroImg) {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        requestAnimationFrame(function () {
          var section = heroImg.closest('.about-hero');
          if (!section) return;
          var rect = section.getBoundingClientRect();
          var vh = window.innerHeight;
          if (rect.bottom < 0 || rect.top > vh) { ticking = false; return; }
          var progress = -rect.top / (rect.height + vh);
          var shift = progress * 30;
          heroImg.style.transform = 'scale(1.04) translateY(' + shift.toFixed(1) + 'px)';
          ticking = false;
        });
        ticking = true;
      }, { passive: true });
    }
  }

  /* ── 4. CRAFTSMANSHIP — text content reveal ──────────────
     .craft-content children fade in when section enters view.
     Respects prefers-reduced-motion like every other transform
     reveal in this file.
  ────────────────────────────────────────────────────────── */
  if (
    'IntersectionObserver' in window &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    var craftSection = document.querySelector('.craftsmanship');
    if (craftSection) {
      var craftContent = craftSection.querySelector('.craft-content');
      if (craftContent) {
        craftContent.style.opacity = '0';
        craftContent.style.transform = 'translateX(20px)';
        craftContent.style.transition = 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s';
        var craftObs = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) {
            craftContent.style.opacity = '1';
            craftContent.style.transform = 'none';
            craftObs.disconnect();
          }
        }, { threshold: 0.2 });
        craftObs.observe(craftSection);
      }
    }
  }

  /* ── 5. FOUNDERS CARD — mouse-tracking tilt (desktop) ────
     Subtle 3D card tilt on mouse movement within card bounds.
  ────────────────────────────────────────────────────────── */
  if (window.matchMedia('(pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.addEventListener('mousemove', function (e) {
      var card = e.target.closest('.about-founders__card');
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var cx = ((e.clientX - rect.left) / rect.width  - 0.5) * 4;
      var cy = ((e.clientY - rect.top)  / rect.height - 0.5) * 2.5;
      card.style.transition = 'transform 0.1s ease, opacity 0.75s ease';
      card.style.transform  = 'perspective(1000px) rotateY(' + cx + 'deg) rotateX(' + (-cy) + 'deg)';
    });
    document.addEventListener('mouseleave', function (e) {
      var card = e.target.closest('.about-founders__card');
      if (card) {
        card.style.transition = 'transform 0.5s ease, opacity 0.75s ease';
        card.style.transform  = '';
      }
    }, true);
  }

}());
